import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  ViewChildren,
  OnInit,
  OnDestroy,
} from '@angular/core';
import {
  findActionByNameAndValue,
  findConditionByNameAndValue,
  RulesOperator,
  RulesActions,
  RulesConditions,
  handleColorCodingBackdropClick,
} from '@app/features/automation-rules/utils';
import { AutomationRulesActions } from '../../../actions';
import { SelectedRuleParams } from '../rule-template-selector.component';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { RuleTemplateService } from '../rule-template.service';
import { MatSelect } from '@angular/material/select';
import { DropdownOption } from '@app/storybook/components/dropdown/dropdown.component';
import { ColorCoding, ColorEnum } from '@pdqdev/pdq-shared/dist/entities';
import { ShopService } from '@app/services/shops/shop.service';
import { Shop } from '@app/services/shops/shop.model';

@Component({
  selector: 'order-tracked-template',
  templateUrl: './order-tracked-template.component.html',
  styleUrls: ['./order-tracked-template.component.scss'],
  standalone: false,
})
export class OrderTrackedTemplateComponent implements AfterViewInit, OnInit, OnDestroy {
  @Input() conditions: Array<any>;
  @Input() actions: Array<any>;
  @Input() selectedRuleParams?: SelectedRuleParams;

  @Input() onChangeConditionSelect: EventEmitter<any>;
  @Input() onChangeActionSelect: EventEmitter<any>;
  @Input() onChangeOperatorSelect: EventEmitter<any>;
  @Input() onChangeOperator2Select: EventEmitter<any>;
  @Input() onRemoveCondition: EventEmitter<any>;
  @ViewChildren('conditionSelect') conditionSelects;
  @ViewChildren('selectList') selectList;

  destroyed$ = new Subject();
  headsUp: string[] = [];

  deliveryStatusOptions: DropdownOption[][][] = [[[null]]];

  // ---Conditions---
  currentOperatorValue: RulesOperator[][] = [[null]];
  currentOperator2Value: RulesOperator[][] = [[null]];
  currentAllConditionsSelectValue: AutomationRulesActions.SelectedCondition[][] = [[null]];
  currentDeliveryStatusValue: DropdownOption[][][] = [[[null]]];

  // ---Action---
  currentActionSelectValue: AutomationRulesActions.SelectedAction;
  currentActionAddTagsValue: string[] = [];
  currentActionDeleteTagsValue: string[] = [];

  currentColorCoding: string[] = [ColorEnum.NO_COLOR];
  currentColorCodingOperator: DropdownOption;
  colorCodingOptions: DropdownOption[] = [];
  colorCodingData: ColorCoding[];
  handleColorCodingBackdropClick = handleColorCodingBackdropClick;

  indexOfLastMatOption: any;
  constructor(
    private shopService: ShopService,
    private changeDetectorRef: ChangeDetectorRef,
    private ruleTemplateService: RuleTemplateService,
  ) {}

  ngOnInit() {
    this.initListeners();
    this.initDropdownOptions();
    this.initSelectedRuleParams();
    this.initShopSettings();
  }

  ngAfterViewInit() {
    this.selectList.changes.subscribe(() => {
      this.conditionSelects.forEach((condition: MatSelect) => {
        if (!this.indexOfLastMatOption) {
          return;
        }
        const [j, i] = this.indexOfLastMatOption;
        const atrb = condition._elementRef.nativeElement.getAttribute('data-2dIndex');
        if (condition._elementRef.nativeElement.getAttribute('data-2dIndex') == j + ',' + i) {
          this.indexOfLastMatOption = null;
          condition.open();
          this.changeDetectorRef.detectChanges();
        }
      });
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
  }

  initListeners() {
    this.onChangeActionSelect.pipe(takeUntil(this.destroyed$)).subscribe((e) => this.setHeadsUpNotifications(e));
  }
  initShopSettings() {
    this.shopService.shop$.pipe(first()).subscribe((shop: Shop) => {
      this.colorCodingData = shop.colorCoding;
    });
  }

  initDropdownOptions() {
    this.deliveryStatusOptions[0][0] = this.ruleTemplateService.convertValuesToDropDownOptions({
      conditions: this.conditions[0].values,
      conditionName: RulesConditions.DeliveryStatus,
      labelField: 'label',
      valueField: 'value',
      multiSelect: true,
    });

    this.colorCodingOptions = this.ruleTemplateService.convertValuesToDropDownOptions({
      conditions: this.actions[0].values,
      conditionName: 'setColorCoding',
      labelField: 'label',
      valueField: 'value',
    });
    this.currentColorCodingOperator = this.colorCodingOptions[0];
  }

  initSelectedRuleParams() {
    this.currentAllConditionsSelectValue = this.getSelectedConditions();
    this.currentActionSelectValue = this.getSelectedAction();

    if (this.currentActionSelectValue) {
      const selectedAction = { ...this.currentActionSelectValue };
      if (selectedAction.name === RulesActions.AddTags) {
        this.tagsChanged(this.currentActionAddTagsValue, 'add');
      } else if (selectedAction.name === RulesActions.DeleteTags) {
        this.tagsChanged(this.currentActionDeleteTagsValue, 'delete');
      } else if (selectedAction.name == RulesActions.ColorCoding && this.currentColorCoding) {
        this.onColorCodingChanged(this.currentColorCoding, this.currentColorCodingOperator);
      } else {
        this.onChangeActionSelect.emit(selectedAction);
      }
    }
  }

  getSelectedConditions() {
    if (!this.selectedRuleParams) {
      return [[null]];
    }

    const selectedConditions = [[]];
    const orWhenLength = this.selectedRuleParams.orWhen.length;

    for (let j = 0; j < orWhenLength; j++) {
      this.addOrCondition(j, false);
      for (let i = 0; i < this.selectedRuleParams.orWhen[j].andWhen.length; i++) {
        this.addCondition([j, i], false);
        const andWhenName: string = Object.keys(this.selectedRuleParams.orWhen[j].andWhen[i])?.pop();
        const andWhenValue: any = Object.values(this.selectedRuleParams.orWhen[j].andWhen[i])?.pop();
        const andWhenValueToCheck = andWhenValue.values ? andWhenValue.values : andWhenValue;
        const condition = this.conditions[0].values.find(
          findConditionByNameAndValue.bind(null, andWhenName, andWhenValueToCheck),
        );

        if (!selectedConditions[j]) {
          selectedConditions[j] = [];
        }
        selectedConditions[j].push(condition);
        switch (andWhenName) {
          case RulesConditions.DeliveryStatus:
            this.currentDeliveryStatusValue[j][i] = andWhenValue.deliveryStatus;
            this.onChangeConditionSelect.emit({ value: { ...condition, value: andWhenValue }, index: [j, i] });
            break;
        }
      }
    }

    return selectedConditions;
  }

  changeConditionSelect({ value: selectedCondition }, indices: [number, number]): void {
    const [j, i] = indices;
    const updatedSelectedCondition = { ...selectedCondition };

    switch (selectedCondition.name) {
      case RulesConditions.DeliveryStatus:
        const deliveryStatus = [];
        this.currentDeliveryStatusValue[j][i] = deliveryStatus;
        updatedSelectedCondition.value = { deliveryStatus };
        break;
    }

    selectedCondition = updatedSelectedCondition;
    this.onChangeConditionSelect.emit({ value: selectedCondition, index: [j, i] });
    this.onResetActionSelectValue();
  }

  changeOperatorSelect(event, indices: [number, number]) {
    const [j, i] = indices;
    let operator;
    let is;

    if (event?.source?.controlType == 'mat-select') {
      if (event.value == 'is') {
        operator = { value: 'is', label: 'is' };
        is = true;
      }
      if (event.value == 'is-not') {
        operator = { value: 'is-not', label: 'is not' };
        is = false;
      }
    } else {
      operator = event;
    }

    this.currentOperatorValue[j][i] = operator;
    this.onChangeOperatorSelect.emit({ operator, is, index: [j, i] });
  }

  changeOperator2Select(operator, indices: [number, number]) {
    const [j, i] = indices;
    this.currentOperator2Value[j][i] = operator;
    this.onChangeOperator2Select.emit({ operator, index: [j, i] });
  }

  getSelectedAction() {
    if (!this.selectedRuleParams) {
      return null;
    }
    const selectedActionName = Object.keys(this.selectedRuleParams.andThen[0])[0];
    const selectedActionValue: any = Object.values(this.selectedRuleParams.andThen[0])[0];
    const actionData = this.actions[0]?.values?.find((a) => a.name === selectedActionName);

    switch (selectedActionName) {
      case RulesActions.AddTags:
        this.currentActionAddTagsValue = selectedActionValue;
        break;
      case RulesActions.DeleteTags:
        this.currentActionDeleteTagsValue = selectedActionValue;
        break;
      case RulesActions.ColorCoding:
        this.currentColorCoding = selectedActionValue.colors;
        this.currentColorCodingOperator = selectedActionValue.operator;
        break;
    }
    return this.actions[0].values.find(findActionByNameAndValue.bind(null, selectedActionName, selectedActionValue));
  }

  changeActionSelect({ value: selectedAction }): void {
    switch (selectedAction.name) {
      default:
        // do not set for multiple values
        if (!selectedAction.values) {
          this.onChangeActionSelect.emit(selectedAction);
        }
    }
  }

  getFormattedText(text: string) {
    return text.replace(/\[\[/g, '<span class="highlight">').replace(/\]\]/g, '</span>');
  }

  getActionText(action: AutomationRulesActions.SelectedAction) {
    const text = `<span class="value">${action.text}</span>`;
    return text;
  }

  onResetActionSelectValue() {
    this.currentActionSelectValue = null;
    this.currentActionAddTagsValue = [];
    this.currentActionDeleteTagsValue = [];
    this.currentColorCoding = [ColorEnum.NO_COLOR];
    this.currentColorCodingOperator = this.colorCodingOptions[0];
    this.onChangeActionSelect.emit(this.currentActionSelectValue);
  }

  setHeadsUpNotifications(action: any) {
    this.headsUp = this.ruleTemplateService.setHeadsUpNotifications(action, this.currentActionSelectValue);
  }

  addOrCondition(index, shouldAddChildCondition = true): void {
    this.currentAllConditionsSelectValue.push([]);
    this.currentOperatorValue.push([]);
    this.currentOperator2Value.push([]);
    this.currentDeliveryStatusValue.push([]);
    this.deliveryStatusOptions.push([[]]);

    if (shouldAddChildCondition) {
      this.addCondition([index, 0]);
    }
  }

  addCondition(indices: [number, number], shouldEmit = true): void {
    const [j, i] = indices;
    this.currentAllConditionsSelectValue[j].push(null);
    this.currentOperatorValue[j].push(null);
    this.currentDeliveryStatusValue[j].push(null);

    this.deliveryStatusOptions[j][i] = this.ruleTemplateService.convertValuesToDropDownOptions({
      conditions: this.conditions[0].values,
      conditionName: RulesConditions.DeliveryStatus,
      labelField: 'label',
      valueField: 'value',
      multiSelect: true,
    });
    if (shouldEmit) {
      this.indexOfLastMatOption = indices;
      this.onChangeConditionSelect.emit({ value: {}, index: [j, i] });
    }
  }

  checkIfAllValuesInserted() {
    const isAllInserted = this.currentAllConditionsSelectValue?.every((conditionGroup, groupIndex) => {
      return conditionGroup.every((currentConditionSelectValue, index) => {
        return this.checkIfAllValuesInsertedForCurrentCondition(currentConditionSelectValue, groupIndex, index);
      });
    });
    return isAllInserted;
  }

  checkIfAllValuesInsertedForConditionGroup(row) {
    const isAllInserted = this.currentAllConditionsSelectValue[row]?.every((currentConditionSelectValue, index) => {
      return this.checkIfAllValuesInsertedForCurrentCondition(currentConditionSelectValue, row, index);
    });
    return isAllInserted;
  }

  checkIfAllValuesInsertedForCurrentCondition(currentConditionSelectValue, groupIndex, index) {
    switch (currentConditionSelectValue?.name) {
      case RulesConditions.DeliveryStatus:
        return !!this.currentDeliveryStatusValue[groupIndex][index]?.length;
    }
    return currentConditionSelectValue?.value;
  }

  removeCondition(indices: [number, number]) {
    const [j, i] = indices;
    this.currentAllConditionsSelectValue[j].splice(i, 1);
    this.currentOperatorValue[j].splice(i, 1);
    this.currentDeliveryStatusValue[j].splice(i, 1);
    this.deliveryStatusOptions[j].splice(i, 1);
    if (j > 0 && i == 0 && this.currentAllConditionsSelectValue[j].length == 0) {
      this.currentAllConditionsSelectValue.splice(j, 1);
      this.currentOperatorValue.splice(j, 1);
      this.currentDeliveryStatusValue.splice(j, 1);
      this.deliveryStatusOptions.splice(j, 1);
    }

    this.onRemoveCondition.emit({ index: [j, i] });
  }

  isActionDisabled(currentAction, actionIndex) {}

  onColorCodingChanged(colors?: string[], operator?: DropdownOption): void {
    if (colors) {
      this.currentColorCoding = colors;
    }
    if (operator) {
      this.currentColorCodingOperator = operator;
    }
    const setColorCodingAction = {
      ...this.currentActionSelectValue,
    };
    setColorCodingAction.value = { colors: this.currentColorCoding, operator: this.currentColorCodingOperator };
    this.onChangeActionSelect.emit(setColorCodingAction);
  }

  tagsChanged(tags: string[], action: 'add' | 'delete') {
    const tagsAction = {
      ...this.currentActionSelectValue,
    };
    switch (action) {
      case 'add':
        this.currentActionAddTagsValue = tags;
        break;
      case 'delete':
        this.currentActionDeleteTagsValue = tags;
        break;
    }

    tagsAction.value = tags;
    this.onChangeActionSelect.emit(tagsAction);
  }

  changeDeliveryStatusSelect(value, indices: [number, number]): void {
    const [j, i] = indices;
    const deliveryStatusCondition = this.ruleTemplateService.changeDeliveryStatusSelect(
      value,
      i,
      this.currentAllConditionsSelectValue[j],
      this.currentDeliveryStatusValue[j],
    );
    this.onChangeConditionSelect.emit({ value: deliveryStatusCondition, index: [j, i] });
  }
}
